/**
 * The Format menu: one short menu of RENDERED choices, opened from a measure's
 * column header (right-click, or the `⋯` beside its label) or from the Values
 * shelf.
 *
 * It replaced a Format Cells–style dialog that stacked a sample box, a
 * rounding list, a five-row negatives list, zero, symbol, accounting and the
 * rule editor into one scrolling panel. Every everyday choice here is ONE
 * click on the value as it will read — `$48.2K`, `($1,234)`, `–` — and the
 * result shows in the table behind the menu, so the table is the preview.
 *
 * Highlights are the one thing that cannot be a click: a rule is a comparison,
 * a threshold, a colour and a position among up to three. They are edited IN
 * the menu, as its last section, rather than on a second screen behind a
 * "Highlight…" item — a view the reader had to find, enter and come back from.
 *
 * WHAT IS OFFERED IS WHAT APPLIES. Every section is gated on
 * `resolved.controls`, the same table the resolver obeys, so a symbol never
 * appears on hours and K/M never on a percentage. Accounting fixes negatives
 * and zero, so those sections step aside while it is on.
 */
import type { FormatRule, ValueFormat, ValueFormatKey } from '@clockwise/semantic-core';
import {
  isRedStyle,
  MAX_FORMAT_RULES,
  NEGATIVE_STYLES,
  RULE_OPS,
  RULE_TONES,
  SCALE_STYLES,
  SYMBOL_STYLES,
  ZERO_STYLES,
} from '@clockwise/semantic-core';
import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { formatResolved } from '../format';
import type { PopupAnchor } from '../render/anchored';
import { Anchored } from '../render/anchored';
import {
  columnSample,
  decimalChoices,
  type FormatContext,
  negativeSample,
  RULE_OP_WORDS,
  roundingSample,
  ruleSentence,
  SCALE_LABELS,
} from './format-options';
import { useDismiss } from './use-dismiss';

/**
 * Open the Format menu for one measure, beside `anchor`, handing focus back to
 * `returnFocus` when it closes. The shelf's Format chip is its only caller.
 */
export type OnFormatColumn = (
  measureId: string,
  anchor: PopupAnchor,
  returnFocus: HTMLElement | null,
) => void;

export interface FormatMenuProps {
  ctx: FormatContext;
  /** `undefined` DELETES the key — absence is what keeps inheriting. */
  onChange: (key: ValueFormatKey, value: ValueFormat[ValueFormatKey]) => void;
  onReset: () => void;
  onClose: () => void;
  /** Whether anything is stored, so Reset only offers itself when it can act. */
  dirty: boolean;
  /** Where to open: under a trigger, or at the pointer of a right-click. */
  anchor: PopupAnchor;
  /**
   * The control that opened the menu, when pressing it again should CLOSE it
   * rather than dismiss-and-reopen in one press. See `useDismiss`.
   */
  toggleRef?: RefObject<HTMLElement | null>;
  /**
   * The column's values as the preview currently holds them, so the Decimals
   * row is drawn with the reader's own number. Absent draws an illustration.
   */
  values?: readonly (number | null | undefined)[];
}

export function FormatMenu({
  ctx,
  onChange,
  onReset,
  onClose,
  dirty,
  anchor,
  toggleRef,
  values,
}: FormatMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  useDismiss(true, ref, onClose, toggleRef);

  // Focus lands on what is currently chosen, so arrows and Tab start from the
  // column's own state rather than from the top of the menu.
  useEffect(() => {
    const target =
      ref.current?.querySelector<HTMLElement>('[aria-pressed="true"]') ??
      ref.current?.querySelector<HTMLElement>('button');
    target?.focus();
  }, []);

  const { resolved, inherited, measure } = ctx;
  const has = (key: ValueFormatKey) => resolved.controls.includes(key);
  const accounting = resolved.accounting;

  const sample = columnSample(values, resolved);

  return (
    <div ref={ref} className="rk-fmtmenu" role="dialog" aria-label={`Format ${measure.label}`}>
      <Anchored panelRef={ref} anchor={anchor} />
      <div className="rk-fmtmenu-head">
        <span className="rk-fmtmenu-title">
          Format <span>· {measure.label}</span>
        </span>
        {/* IN THE HEADER, not a row of its own at the foot: a row that
                appears on the first change grew the menu by its height, and a
                menu clamped to the bottom of the viewport jumped up to make
                room. The header's height does not move either way. */}
        <span className="rk-fmtmenu-actions">
          {dirty && (
            <button type="button" className="rk-linkbtn" onClick={onReset}>
              Reset
            </button>
          )}
          <button type="button" className="rk-icon" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </span>
      </div>

      {has('decimals') && (
        <Section label="Decimals" note={`default ${inherited.decimals}`}>
          <div className="rk-fmtseg">
            {decimalChoices(ctx).map((places) => (
              <Choice
                key={places}
                pressed={resolved.decimals === places}
                label={`${places} decimal place${places === 1 ? '' : 's'}`}
                isDefault={places === inherited.decimals}
                // Deleted, not stored, when it lands back on the default.
                onClick={() =>
                  onChange('decimals', places === inherited.decimals ? undefined : places)
                }
              >
                {roundingSample(sample, resolved, { decimals: places })}
              </Choice>
            ))}
          </div>
        </Section>
      )}

      {has('scale') && (
        <Section label="Scale">
          <div className="rk-fmtseg">
            {(['none', ...SCALE_STYLES] as const).map((scale) => (
              <Choice
                key={scale}
                pressed={resolved.scale === scale}
                label={scale === 'none' ? 'No scale' : `In ${scale} (${SCALE_LABELS[scale]})`}
                title={roundingSample(sample, resolved, { scale })}
                onClick={() => onChange('scale', scale === 'none' ? undefined : scale)}
              >
                {SCALE_LABELS[scale]}
              </Choice>
            ))}
          </div>
        </Section>
      )}

      {has('negative') && !accounting && (
        <Section label="Negatives">
          <div className="rk-fmtseg">
            {NEGATIVE_STYLES.map((style) => (
              <Choice
                key={style}
                pressed={resolved.negative === style}
                red={isRedStyle(style)}
                // Colour alone carries `redOnly`, so the name has to say it.
                label={style === 'redOnly' ? 'Red, no minus sign' : undefined}
                onClick={() => onChange('negative', style === 'minus' ? undefined : style)}
              >
                {/* The SIGN STYLE is the whole question here, so the sample drops
                        what the column's own cells already show — symbol, scale,
                        places. Five `($1,234.10K)` do not fit on one row. */}
                {negativeSample(style, {
                  ...resolved,
                  decimals: 0,
                  scale: 'none',
                  symbol: 'none',
                })}
              </Choice>
            ))}
          </div>
        </Section>
      )}

      {has('zero') && !accounting && (
        <Section label="Zero">
          <div className="rk-fmtseg">
            {ZERO_STYLES.map((style) => (
              <Choice
                key={style}
                pressed={resolved.zero === style}
                label={`Show zero as ${style === 'blank' ? 'blank' : style === 'dash' ? 'a dash' : 'a number'}`}
                onClick={() => onChange('zero', style === 'number' ? undefined : style)}
              >
                {style === 'number' ? (
                  formatResolved(0, { ...resolved, zero: 'number' })
                ) : style === 'dash' ? (
                  '-'
                ) : (
                  <em>blank</em>
                )}
              </Choice>
            ))}
          </div>
        </Section>
      )}

      {has('symbol') && (
        <Section label="Symbol">
          <div className="rk-fmtseg">
            {SYMBOL_STYLES.map((style) => (
              <Choice
                key={style}
                pressed={resolved.symbol === style}
                onClick={() => onChange('symbol', style === 'symbol' ? undefined : style)}
              >
                {style === 'symbol' ? '$' : style === 'code' ? 'USD' : 'None'}
              </Choice>
            ))}
          </div>
        </Section>
      )}

      {(has('accounting') || has('separators')) && (
        <div className="rk-fmtmenu-sec rk-fmtmenu-toggles">
          {has('separators') && (
            <label className="rk-fmtcheck">
              <input
                type="checkbox"
                checked={resolved.separators}
                onChange={(e) => onChange('separators', e.target.checked ? undefined : false)}
              />
              <span>1,000 separator</span>
            </label>
          )}
          {has('accounting') && (
            <label className="rk-fmtcheck">
              <input
                type="checkbox"
                checked={accounting}
                onChange={(e) => onChange('accounting', e.target.checked ? true : undefined)}
              />
              <span>Accounting</span>
              {accounting && <span className="rk-fmthint">— brackets, and – for zero</span>}
            </label>
          )}
        </div>
      )}

      {has('rules') && (
        <Section label="Highlight">
          <RuleEditor
            ctx={ctx}
            onChange={(next) => onChange('rules', next.length === 0 ? undefined : next)}
          />
        </Section>
      )}
    </div>
  );
}

function Section({
  label,
  note,
  children,
}: {
  label: string;
  /** Muted text after the label — room the options themselves do not have. */
  note?: string;
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset's border and legend fight the menu's own rhythm
    <div className="rk-fmtmenu-sec" role="group" aria-label={label}>
      <span className="rk-fmtlabel">
        {label}
        {note && <span className="rk-fmtnote"> · {note}</span>}
      </span>
      {children}
    </div>
  );
}

/** One rendered choice: the value as it will read IS the button's face. */
function Choice({
  pressed,
  onClick,
  children,
  red,
  label,
  title,
  isDefault,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
  red?: boolean;
  label?: string;
  title?: string;
  /**
   * Marks the inherited choice for a screen reader. The visible marker is on
   * the row's label: a tag inside a joined option truncated wide values.
   */
  isDefault?: boolean;
}) {
  return (
    <button
      type="button"
      className={`rk-fmtseg-opt${red ? ' rk-neg' : ''}`}
      aria-pressed={pressed}
      aria-label={label && isDefault ? `${label} (default)` : label}
      title={title ?? label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * The highlight rules editor.
 *
 * The one control in this popup that edits a LIST of records rather than a
 * scalar, which is why it is a component instead of a branch in the walk over
 * the rendered choices above — see `ControlKind` for the full reasoning.
 *
 * ORDER IS THE PRECEDENCE, so the rows are reorderable and the list is drawn in
 * the order it is stored. A reader who cannot see which rule wins has to guess,
 * and the guess ("the most specific one", "the last one") is wrong as often as
 * it is right.
 */
function RuleEditor({
  ctx,
  onChange,
}: {
  ctx: FormatContext;
  onChange: (next: FormatRule[]) => void;
}) {
  const { resolved, measure } = ctx;
  const rules = resolved.rules;

  // THE TYPED TEXT, held separately from the committed number.
  //
  // A threshold input that re-derived its own value from the parsed rule on
  // every keystroke cannot be typed into: "-" is not a number, so a reader
  // entering -40 has the minus deleted under them before they reach the 4, and
  // "1." collapses to "1" the moment they try to reach "1.5". The draft holds
  // what was typed; the rule holds the last thing that parsed.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const draftFor = (index: number, rule: FormatRule) => drafts[index] ?? String(rule.value);

  const replace = (index: number, patch: Partial<FormatRule>) =>
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));

  const move = (index: number, step: number) => {
    const next = [...rules];
    const [taken] = next.splice(index, 1);
    if (taken === undefined) return;
    next.splice(index + step, 0, taken);
    // The drafts are keyed by POSITION, and the positions just changed. Anything
    // uncommitted would land on the wrong row, so they go.
    setDrafts({});
    onChange(next);
  };

  return (
    <div className="rk-fmtcol">
      {rules.length > 0 && (
        <ul className="rk-fmtrules">
          {/* POSITION IS THE IDENTITY. A rule has no id, and its index is
              what decides which of two matching rules wins, so the index is
              the honest key rather than a fallback for a missing one. Reorder
              and remove both clear the drafts for exactly this reason — see
              `drafts` above. */}
          {rules.map((rule, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the index IS this row's identity, see above
            <li key={index} className="rk-fmtrule">
              <select
                aria-label={`Comparison for highlight rule ${index + 1}`}
                value={rule.op}
                onChange={(e) => replace(index, { op: e.target.value as FormatRule['op'] })}
              >
                {RULE_OPS.map((op) => (
                  <option key={op} value={op}>
                    {RULE_OP_WORDS[op]}
                  </option>
                ))}
              </select>

              {/* NOT `type="number"`: the scroll wheel edits those silently
                  while the page scrolls past it. Same rule as `Stepper`. */}
              <input
                type="text"
                inputMode="decimal"
                className="rk-fmtrule-value"
                aria-label={`Threshold for highlight rule ${index + 1}`}
                value={draftFor(index, rule)}
                onChange={(e) => {
                  const text = e.target.value;
                  setDrafts((d) => ({ ...d, [index]: text }));
                  const parsed = Number(text);
                  if (text.trim() !== '' && Number.isFinite(parsed)) {
                    replace(index, { value: parsed });
                  }
                }}
                onBlur={() => setDrafts((d) => ({ ...d, [index]: String(rule.value) }))}
              />

              <span className="rk-fmtrule-tones">
                {RULE_TONES.map((tone) => (
                  <button
                    key={tone}
                    type="button"
                    className={`rk-fmtswatch rk-tone rk-tone-${tone}${
                      rule.tone === tone ? ' rk-fmtswatch-on' : ''
                    }`}
                    aria-label={`${tone} for highlight rule ${index + 1}`}
                    aria-pressed={rule.tone === tone}
                    onClick={() => replace(index, { tone })}
                  >
                    A
                  </button>
                ))}
              </span>

              <button
                type="button"
                className="rk-icon"
                aria-label={`Move highlight rule ${index + 1} up`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className="rk-icon"
                aria-label={`Move highlight rule ${index + 1} down`}
                disabled={index === rules.length - 1}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
              <button
                type="button"
                className="rk-icon"
                aria-label={`Remove highlight rule ${index + 1}`}
                onClick={() => {
                  setDrafts({});
                  onChange(rules.filter((_, i) => i !== index));
                }}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className="rk-linkbtn"
        disabled={rules.length >= MAX_FORMAT_RULES}
        onClick={() => onChange([...rules, { op: 'lt', value: 0, tone: 'red' }])}
      >
        Add rule
      </button>

      <p className="rk-fmthint">
        {/* The rules READ BACK as a sentence, in the order they are applied. A
            reader checking three thresholds should not have to hold the
            precedence in their head while doing it. */}
        {rules.length > 0 && (
          <>
            {rules.map((rule) => ruleSentence(rule, resolved)).join(', ')}.{' '}
            {rules.length > 1 ? 'The first match wins. ' : ''}
          </>
        )}
        {resolved.format === 'percent'
          ? 'Thresholds are percentages, as shown — type 100 for 100%.'
          : resolved.scale !== 'none'
            ? 'Thresholds are whole values — type 1500000 for 1.5M.'
            : `Thresholds are in the units ${measure.label} is shown in.`}
      </p>
    </div>
  );
}
